import { Engine3d } from '../bridge/Engine3d';
import { MeshAssembly } from '../bridge/MeshAssembly';
import { Report } from '../interfaces';
import { Node } from '../elements/Node';
import { Camera } from '../elements/Camera';
import {
	Component,
	MeshRenderer,
	Interactor,
	Collider,
	Point,
	LineRenderer,
	Text,
} from '../elements/component';
import {
	Processor,
	MeshRendererProcessor,
	InteractorProcessor,
	ColliderProcessor,
	PointProcessor,
	LineRendererProcessor,
	TextProcessor,
	ComponentInnerState,
} from '../elements/processor';
import { TransformPosition, PointerHit, AssetsPackage } from '../interfaces';
import { Vector3 } from '@babylonjs/core';

export interface ISceneDelegate {
	onSceneReady(scene: Scene): void;
	onPointerOver?(nodeIds: string | Array<string>, interactor?: Interactor | null): void;
	onCollision?(collider: Report, collisions?: Array<Report>): void;
	onCollisionEnd?(collider: Report): void;
	onSceneResized(scene: Scene): void;
	onBeforeUpdate?(scene: Scene): void; // TODO implement
	onUpdate?(scene: Scene): void; // TODO implement
	onCameraUpdated(camera: Camera): void;
}

export const TAG_GROUND = 'ground';

/**
 * Scene class.
 */
export class Scene {
	public sceneNodes: Map<string, Node>;
	public preloadedMeshes: Map<string, MeshAssembly>;
	public basicElements: Map<string, MeshAssembly>;
	public cameras: Map<string, Camera>;

	protected colliderProcessor: ColliderProcessor;
	protected interactorProcessor: InteractorProcessor;
	protected meshRendererProcessor: MeshRendererProcessor;
	protected pointProcessor: PointProcessor;
	protected lineRendererProcessor: LineRendererProcessor;
	protected textProcessor: TextProcessor;

	protected engine: Engine3d;
	protected delegate: ISceneDelegate;

	private readonly componentStates: Map<Component, ComponentInnerState>;

	constructor(canvas: HTMLCanvasElement, delegate: ISceneDelegate) {
		this.sceneNodes = new Map<string, Node>();
		this.preloadedMeshes = new Map<string, MeshAssembly>();
		this.basicElements = new Map<string, MeshAssembly>();
		this.cameras = new Map<string, Camera>();

		this.engine = new Engine3d(canvas);
		this.delegate = delegate;

		this.componentStates = new Map<Component, ComponentInnerState>();

		this.launch();
	}

	/**
	 * Initializes engine and creates a scene.
	 */
	private launch(): void {
		this.engine.Initiate();
		this.engine.onSceneInit.add(() => {
			this.setupProcessors();
			this.engine.registerBeforeRender(() => this.updateNodes());
			this.setupDelegates();
		});
	}

	/**
	 * Setup update processors.
	 */
	private setupProcessors(): void {
		this.colliderProcessor = new ColliderProcessor(this.engine, this.componentStates);
		this.meshRendererProcessor = new MeshRendererProcessor(this.engine, this.componentStates);
		this.interactorProcessor = new InteractorProcessor(this.engine, this.componentStates);
		this.pointProcessor = new PointProcessor(this.engine, this.componentStates);
		this.lineRendererProcessor = new LineRendererProcessor(this.engine, this.componentStates);
		this.textProcessor = new TextProcessor(this.engine, this.componentStates);
	}

	/**
	 * Setup delegates.
	 */
	private setupDelegates(): void {
		if (this.delegate) {
			this.delegate.onSceneReady(this);
			if (this.delegate.onPointerOver) {
				this.engine.onScenePick.add((pi) => {
					if (pi.hit && pi.pickedMesh && pi.pickedMesh.name !== TAG_GROUND) {
						const nodeId = this.engine.pointerManager.getPickableReporters(pi.pickedMesh);
						if (nodeId) {
							if (this.delegate.onPointerOver) {
								this.delegate.onPointerOver(nodeId, null);
							} else {
								throw 'Scene: setupDelegates: this.delegate.onPointerOver does not exist';
							}
						}
					}
				});
			}
			if (this.delegate.onCollision) {
				this.engine.onCollisionReport.add((tuple) => {
					const colliders = this.engine.colissionManager.getCollisionReporters(tuple[0]);
					const collisions = new Array<Report>();
					tuple[1].forEach((m) => {
						const set = this.engine.colissionManager.getCollisionReporters(m);
						if (set.size) {
							set.forEach((i) => collisions.push(i));
						}
					});
					if (colliders && collisions.length) {
						colliders.forEach((c) => {
							if (this.delegate.onCollision) {
								this.delegate.onCollision(c, collisions);
							} else {
								throw 'Scene: setupDelegates: this.delegate.onCollision does not exist';
							}
						});
					}
				});
			}
			if (this.delegate.onCollisionEnd) {
				this.engine.onCollisionEndReport.add((m) => {
					const colliders = this.engine.colissionManager.getCollisionReporters(m);
					if (colliders) {
						colliders.forEach((c) => {
							if (this.delegate.onCollisionEnd) {
								this.delegate.onCollisionEnd(c);
							} else {
								throw 'Scene: setupDelegates: this.delegate.onCollisionEnd does not exist';
							}
						});
					}
				});
			}
			if (this.delegate.onCameraUpdated) {
				this.engine.onCameraViewUpdated.add((camera) => {
					const camera2 = this.cameras.get(camera.id);
					if (camera2) {
						this.delegate.onCameraUpdated(camera2);
					} else {
						throw 'Scene: setupDelegates: camera does not exist';
					}
				});
			}
			if (this.delegate.onSceneResized) {
				this.engine.onSceneResized.add(() => {
					this.delegate.onSceneResized(this);
				});
			}
		}
	}

	/**
	 * Starts render loop.
	 */
	public startRenderLoop(): void {
		this.engine.startRenderLoop();
	}

	/**
	 * Stop the render loop.
	 */
	public stopRenderLoop(): void {
		this.engine.stopRenderLoop();
	}

	/**
	 * Preloads model.
	 */
	public async preload(url: string): Promise<MeshAssembly> {
		const m = await this.engine.meshFactory.loadGltf(url);
		this.preloadedMeshes.set(url, m);
		return m;
	}

	/**
	 * Check if url is preloaded.
	 */
	public isPreloaded(url: string): boolean {
		return this.preloadedMeshes.has(url);
	}

	/**
	 * Preloads package.
	 */
	public async preloadPackage(assetsPackage: AssetsPackage): Promise<MeshAssembly[]> {
		const assemblies = await this.engine.meshFactory.loadAssetsPackage(assetsPackage);
		assemblies.forEach((a) => this.preloadedMeshes.set(a.name, a));
		return assemblies;
	}

	/**
	 * Creates scene Node.
	 *
	 */
	public createNode(id: string, pos: TransformPosition = { x: 0, y: 0, z: 0 }): Node {
		const assembly = this.engine.meshFactory.createEmpty(id);
		const node = new Node(assembly, id);
		node.setAbsolutePosition(pos);
		this.sceneNodes.set(id, node);
		return node;
	}

	/**
	 * Destroys Node.
	 *
	 */
	public destroyNode(id: string): void {
		const node = this.sceneNodes.get(id);
		if (node) {
			node.disconnect();
			node.components.forEach((c) => {
				const processor = this.getProcessor(c);
				if (processor) {
					processor.destroy(c, node);
				}
			});
			node.components.clear();
			node.isDestroyed = true;
		} else {
			throw 'Scene: destroyNode: node does not exist';
		}
	}

	/**
	 * Creates camera.
	 */
	public createCamera(id: string): Camera {
		const canvas = this.engine.getCanvas();
		const manager = this.engine.cameraManager;
		manager.createCamera(canvas, id);
		const camera = new Camera(manager, id);
		this.cameras.set(id, camera);
		return camera;
	}

	/**
	 * Creates ground.
	 */
	public createGround(): void {
		const ground = this.engine.meshFactory.createGround(TAG_GROUND);
		this.basicElements.set(TAG_GROUND, ground);
		ground.transformNode.getChildMeshes().forEach((mesh) => {
			mesh.isPickable = true;
			this.engine.pointerManager.registerPickable(mesh, TAG_GROUND);
		});
		this.engine.onScenePick.add((pi) => {
			if (pi.hit && pi.pickedMesh && pi.pickedMesh.name == TAG_GROUND) {
				if (this.delegate && this.delegate.onPointerOver) {
					this.delegate.onPointerOver(TAG_GROUND, null);
				}
			}
		});
	}

	/**
	 * Creates light.
	 */
	public createLight(direction: TransformPosition, intensity: number): void {
		const dir = new Vector3(direction.x, direction.y, direction.z);
		this.engine.lightFactory.createLight(dir, intensity);
	}

	/**
	 * Creates Environment.
	 */
	public createEnvironment(url: string, intensity: number): void {
		this.engine.meshFactory.environmentIntensity = intensity;
		this.engine.lightFactory.createEnvironment(url);
	}

	/**
	 * Get pointer hit on scene.
	 *
	 * @param x Screen space.
	 * @param y Screen space.
	 */
	public getPointerHit(x: number, y: number, target?: string): PointerHit | false {
		if (target) {
			if (this.basicElements.has(target)) {
				return this.engine.pointerManager.pointerPick(x, y, this.basicElements.get(target));
			}
			if (this.sceneNodes.has(target)) {
				const node = this.sceneNodes.get(target);
				if (node) {
					const { assembly } = node;
					return this.engine.pointerManager.pointerPick(x, y, assembly);
				} else {
					throw 'Scene: getPointerHit: node does not exist';
				}
			}
			return false;
		}
		return this.engine.pointerManager.pointerPick(x, y);
	}

	/**
	 * Set debug mode.
	 *
	 */
	public set debug(value: boolean) {
		this.colliderProcessor.debug = value;
		this.interactorProcessor.debug = value;
		this.meshRendererProcessor.debug = value;
	}

	public showDebug(visible: boolean, elementId: string): void {
		this.engine.showInspector(visible, elementId);
	}

	/**
	 * Get if device uses iOS.
	 *
	 */
	public isIOS(): boolean {
		return this.engine.isIOS();
	}

	/**
	 * Set camera control.
	 */
	public set controlCamera(value: boolean) {
		if (value) {
			this.engine.cameraManager.attachControl(this.engine.getCanvas());
		} else {
			this.engine.cameraManager.detachControl(this.engine.getCanvas());
		}
	}

	/**
	 * Updates nodes.
	 */
	protected updateNodes(): void {
		this.sceneNodes.forEach((node) => {
			node.components.forEach((component) => {
				const processor = this.getProcessor(component);
				if (processor) {
					processor.update(component, node);
				}
			});

			if (node.isDestroyed) {
				this.sceneNodes.delete(node.id);
				node.assembly.destroy();
			}
		});
	}

	private getProcessor(component: Component): Processor | null {
		if (component instanceof Collider) {
			return this.colliderProcessor;
		}
		if (component instanceof Interactor) {
			return this.interactorProcessor;
		}
		if (component instanceof MeshRenderer) {
			return this.meshRendererProcessor;
		}
		if (component instanceof Point) {
			return this.pointProcessor;
		}
		if (component instanceof LineRenderer) {
			return this.lineRendererProcessor;
		}
		if (component instanceof Text) {
			return this.textProcessor;
		}

		return null;
	}

	/**
	 * Resize engine.
	 */
	public resize(isMobile: boolean): void {
		this.engine.resize(isMobile);
	}

	/**
	 * Destroy the scene.
	 */
	public destroy(): void {
		this.engine.destroy();
	}
}
